import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Image as ImageIcon, WarningCircle } from "@phosphor-icons/react";

export default function AIBuilder() {
  const [f, setF] = useState({ name: "", brand: "", category: "", weight: "", description: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imgResult, setImgResult] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);

  const generate = async () => {
    if (!f.name) return toast.error("Product name is required");
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/ai/build-product", f);
      setResult(r.data);
    } catch { toast.error("Generation failed"); }
    finally { setLoading(false); }
  };

  const analyzeImage = async () => {
    if (!imageFile) return toast.error("Select an image first");
    setImgLoading(true); setImgResult(null);
    const fd = new FormData();
    fd.append("file", imageFile);
    try {
      const r = await api.post("/ai/analyze-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImgResult(r.data);
      // Prefill main form with high-confidence fields
      const suggested = r.data.suggestions || {};
      setF((prev) => ({
        ...prev,
        name: prev.name || suggested.title || "",
        category: prev.category || suggested.category || "",
      }));
      toast.success("Image analyzed");
    } catch { toast.error("Vision analysis failed"); }
    finally { setImgLoading(false); }
  };

  const saveAsProduct = async () => {
    setSaving(true);
    try {
      await api.post("/products", {
        name: result.title || f.name, brand: f.brand, category: result.category || f.category,
        description: result.long_description || result.short_description,
      });
      toast.success("Product created — visit Products page");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="ai-builder-page" className="space-y-6 min-w-0">
      <div>
        <div className="eyebrow mb-1">AI Product Builder</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter text-navy">Enter the basics. Get the whole listing.</h1>
        <p className="text-muted-foreground mt-2 text-sm">Powered by GPT-5.6 Terra. Upload an image for real vision analysis.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 min-w-0">
        <div className="space-y-4 min-w-0">
          {/* Image analyzer */}
          <div className="card-base p-6">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon size={18} weight="duotone" className="text-orange"/>
              <div className="section-title">AI Image Analysis</div>
            </div>
            <input data-testid="ai-image-file" type="file" accept="image/*" onChange={(e)=>setImageFile(e.target.files?.[0])} className="text-sm"/>
            <button data-testid="ai-analyze-image" disabled={imgLoading || !imageFile} onClick={analyzeImage} className="btn-secondary mt-3">
              {imgLoading ? "Analyzing…" : "Analyze image"}
            </button>
            {imgResult && (
              <div className="mt-4 space-y-2 text-sm">
                <div className="pill pill-draft"><WarningCircle size={12}/> Verify before publishing</div>
                {Object.entries(imgResult.suggestions || {}).map(([k, v]) => v && (
                  <div key={k} className="border-b border-border pb-2 last:border-0">
                    <div className="eyebrow">{k.replace(/_/g," ")}</div>
                    <div className="font-mono-data text-xs whitespace-pre-wrap text-navy">
                      {Array.isArray(v)
                        ? v.map((x, i) => <div key={i}>• {typeof x === "object" ? JSON.stringify(x) : String(x)}</div>)
                        : typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Text builder */}
          <div className="card-base p-6 space-y-4">
            <div className="section-title">Product basics</div>
            {[["name","Product Name*"],["brand","Brand"],["category","Category"],["weight","Weight / Size"]].map(([k,label])=>(
              <div key={k}>
                <label className="eyebrow block mb-1.5">{label}</label>
                <input data-testid={`ai-${k}`} value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})} className="input-base"/>
              </div>
            ))}
            <div>
              <label className="eyebrow block mb-1.5">Basic Description</label>
              <textarea rows={3} value={f.description} onChange={(e)=>setF({...f,description:e.target.value})} className="input-base"/>
            </div>
            <button data-testid="ai-generate" disabled={loading} onClick={generate} className="btn-primary">
              <Sparkle size={14} weight="fill"/> {loading ? "Generating…" : "AI Complete Product"}
            </button>
          </div>
        </div>

        <div className="card-base p-6 min-w-0">
          <div className="flex items-center justify-between mb-4 gap-2">
            <div className="section-title">Generated Listing</div>
            {result && !result.raw && (
              <button data-testid="ai-save-product" disabled={saving} onClick={saveAsProduct} className="btn-primary py-1.5 px-3 text-[11px]">
                {saving?"Saving…":"Save to Catalog"}
              </button>
            )}
          </div>
          {!result && <div className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border rounded-lg">Fill the form and hit generate. AI output appears here.</div>}
          {result?.raw && <pre className="whitespace-pre-wrap text-xs font-mono-data text-navy">{result.raw}</pre>}
          {result && !result.raw && (
            <div className="space-y-4 text-sm">
              {Object.entries(result).map(([k, v]) => (
                <div key={k} className="border-b border-border pb-3 last:border-0">
                  <div className="eyebrow mb-1">{k.replace(/_/g," ")}</div>
                  <div className="font-mono-data whitespace-pre-wrap text-xs leading-relaxed text-navy">
                    {Array.isArray(v) ? v.map((x,i)=><div key={i}>• {typeof x === "string" ? x : JSON.stringify(x)}</div>)
                     : typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
